/**
 * Admin · Template approvals (002 editor-approval / 003 catalog). Lists templates
 * whose current version is PENDING (`listTemplates({ status: "Pending" })`),
 * resolves the pending version via `listVersions`, and lets a tenant_admin
 * Approve / Reject (with a reason). IMPORTANT: approve/reject route by version
 * NUMBER (the cloud system-of-record key), not the version id. Refreshes on every
 * action so the create → submit → approve → activate loop closes here.
 *
 * UI/UX feedback item 10 (front-end):
 *  - Inline review-comment thread per pending item (`listComments`/`addComment`),
 *    lazy-loaded on expand, with public/internal badges + an add form.
 *  - Terminal rejection: an opt-in "archive permanently" checkbox routes
 *    `terminal:true` to `rejectVersion(...)`. Default stays non-terminal (Draft bounce).
 *  - "Open in Editor" link per item, navigating via the app's hash router.
 *
 * TODO(item 10): consolidate the `approvals_enabled` toggle out of the AI-providers
 * tab (ProvidersTab) into this tab, and merge brand pending items into a unified
 * queue. Deferred — toggle lives in ProvidersTab for now.
 */
import { useEffect, useState } from "react";
import { Accordion } from "../ui/Accordion.js";
import { ActionBar } from "../ui/ActionBar.js";
import { EmptyState } from "../ui/EmptyState.js";
import { Icon } from "../ui/Icon.js";
import { InfoHint } from "../ui/Tooltip.js";
import { useServices } from "../app/context.js";
import { cacheEpoch, readCache, writeCache } from "../stores/cache.js";
import type { ProductTemplate, TemplateVersion, ReviewComment, CommentType } from "@genz/platform";

/** Cache key — seeds the pending list + gate instantly on tab re-open (cache-first). */
const APPROVALS_CACHE = "admin.pendingApprovals";
type ApprovalsSnapshot = { pending: Pending[]; approvalsEnabled: boolean };

interface Pending {
  template: ProductTemplate;
  version: TemplateVersion;
}

/** Jump to the Editor (template authoring) — same hash mechanism the rest of the app
 *  uses (TemplatesPanel/TemplateEditor `window.location.hash = …`). No per-template
 *  deep link exists in the hash router, so this lands on the Editor's Templates tab. */
function openInEditor() {
  if (typeof window !== "undefined") window.location.hash = "editor";
}

export function ApprovalsTab() {
  const { client } = useServices();
  // Cache-first: seed the pending list + gate so re-opening the tab is instant.
  const cachedApprovals = readCache<ApprovalsSnapshot>(APPROVALS_CACHE);
  const [pending, setPending] = useState<Pending[]>(cachedApprovals?.pending ?? []);
  const [loading, setLoading] = useState(cachedApprovals == null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [reasons, setReasons] = useState<Record<string, string>>({});
  // Per-item opt-in for terminal (archive) rejection. Default false → bounce to Draft.
  const [terminal, setTerminal] = useState<Record<string, boolean>>({});
  /* The open submission (Z0 fix round F-6, controlled `Accordion`).
     `undefined` is "the reviewer has not picked one yet", which is NOT the same as picking
     none: the head of the queue opens on arrival, and closing it really does leave the queue
     collapsed. A key that is no longer pending — the submission they just approved — falls
     back to the new head, which is how approving one opens the next. Derived, so there is no
     effect to keep in step and no remount. */
  const [chosen, setChosen] = useState<string | null | undefined>(undefined);
  // Platform-global gate. When OFF (default), templates auto-activate and nothing
  // ever lands here — surface that explicitly instead of a generic empty state.
  const [approvalsEnabled, setApprovalsEnabled] = useState(cachedApprovals?.approvalsEnabled ?? true);

  async function refresh() {
    const cached = readCache<ApprovalsSnapshot>(APPROVALS_CACHE);
    if (!cached) setLoading(true);  // only spin when there's nothing cached to show
    setError(null);
    const epoch = cacheEpoch();
    try {
      let enabled = approvalsEnabled;
      try {
        enabled = (await client.approvalPolicy()).approvals_enabled;
        setApprovalsEnabled(enabled);
      } catch {
        /* keep prior value if the policy read fails */
      }
      const templates = await client.listTemplates({ status: "Pending" });
      const resolved: Pending[] = [];
      for (const t of templates) {
        const versions = await client.listVersions(t.templateId);
        const v = versions.find((x) => x.status === ("Pending" as TemplateVersion["status"]));
        if (v) resolved.push({ template: t, version: v });
      }
      setPending(resolved);
      writeCache(APPROVALS_CACHE, { pending: resolved, approvalsEnabled: enabled }, epoch);
    } catch (e) {
      if (!cached) setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  async function approve(p: Pending) {
    setBusy(p.template.templateId);
    setError(null);
    try {
      await client.approveVersion(p.template.templateId, p.version.versionNumber);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function reject(p: Pending) {
    const reason = (reasons[p.template.templateId] ?? "").trim();
    if (!reason) {
      setError("A reason is required to reject a version.");
      return;
    }
    setBusy(p.template.templateId);
    setError(null);
    try {
      await client.rejectVersion(
        p.template.templateId,
        p.version.versionNumber,
        reason,
        terminal[p.template.templateId] ?? false,
      );
      setReasons((r) => ({ ...r, [p.template.templateId]: "" }));
      setTerminal((t) => ({ ...t, [p.template.templateId]: false }));
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  const openId =
    chosen != null && pending.some((p) => p.template.templateId === chosen)
      ? chosen
      : chosen === null
        ? null
        : (pending[0]?.template.templateId ?? null);

  return (
    <section className="admin-tab" data-testid="admin-approvals">
      <div className="admin-panel__head admin-panel__head--flush">
        <div>
          <strong className="admin-panel__title panel-title">Template approvals <InfoHint text="Finished templates waiting for review before they can be used." /></strong>
        </div>
        <button type="button" className="btn btn--sm" onClick={() => void refresh()} data-testid="approvals-refresh" title="Refresh the pending queue">
          Refresh
        </button>
      </div>

      {error && <p role="alert">{error}</p>}

      {!approvalsEnabled && (
        <EmptyState
          icon="power"
          title="Approvals are turned off"
          hint={<>Templates activate immediately when finished — there's no review queue. A super-admin can turn approvals back on under <strong>AI providers → Template approvals</strong>.</>}
          testId="approvals-disabled"
        />
      )}

      {approvalsEnabled && (loading ? (
        <p className="faint" data-testid="approvals-loading">Loading pending approvals…</p>
      ) : pending.length === 0 ? (
        <EmptyState
          icon="check"
          title="Nothing to approve"
          hint="Submitted template versions awaiting approval will appear here."
          testId="approvals-empty"
        />
      ) : (
        /* Rule 8 — one submission open at a time.
            Every pending item rendered fully expanded, each with its own `.btn--primary`
            Approve, so a queue of six put six loud invitations on one screen. The queue is an
            accordion now: the reviewer opens one submission, reads its thread, and Approve is
            the one primary on screen. The per-item commit is unchanged and every testid
            survives.
            CONTROLLED (Z0 fix round F-6). The queue used to be re-keyed on its own contents —
            `key={ids}` — so that approving the open item opened the next one. A `key=` change
            remounts the WHOLE queue, throwing away the scroll position inside every other
            comment thread and the caret in every other rejection reason. `openKey` moves the
            open section and leaves the rest of the queue standing. */
        <div data-testid="approvals-list">
        <Accordion
          name="admin-approvals"
          openKey={openId}
          onOpenChange={setChosen}
          items={pending.map((p) => ({
            key: p.template.templateId,
            head: p.template.name,
            meta: <span className="badge badge--warn">Pending · v{p.version.versionNumber}</span>,
            body: (
              <div data-testid="approval-item">
                <div className="row row--between">
                  <button
                    type="button"
                    className="btn btn--sm btn--ghost"
                    onClick={openInEditor}
                    data-testid="approval-open-editor"
                    title="Open the template authoring view in the Editor"
                  >
                    Open in Editor
                  </button>
                </div>
              {p.version.submittedBy && <p className="faint">Submitted by {p.version.submittedBy}</p>}

              <CommentThread templateId={p.template.templateId} version={p.version.versionNumber} />

              <div className="field">
                <label htmlFor={`reason-${p.template.templateId}`}>Rejection reason</label>
                <input
                  id={`reason-${p.template.templateId}`}
                  type="text"
                  value={reasons[p.template.templateId] ?? ""}
                  onChange={(e) => setReasons((r) => ({ ...r, [p.template.templateId]: e.target.value }))}
                  placeholder="Required to reject"
                  data-testid="approval-reason"
                />
              </div>
              <div className="field">
                <div className="row field--inline-check">
                  <input
                    id={`terminal-${p.template.templateId}`}
                    type="checkbox"
                    checked={terminal[p.template.templateId] ?? false}
                    onChange={(e) => setTerminal((t) => ({ ...t, [p.template.templateId]: e.target.checked }))}
                    data-testid="approval-terminal"
                  />
                  <label htmlFor={`terminal-${p.template.templateId}`} className="faint">
                    Reject permanently (archived) — skip the Draft bounce-back
                  </label>
                </div>
              </div>
                <ActionBar
                  label={`${p.template.name} review actions`}
                  primary={
                    <button
                      type="button"
                      className="btn btn--primary"
                      onClick={() => approve(p)}
                      disabled={busy === p.template.templateId}
                      data-testid="approval-approve"
                    >
                      Approve
                    </button>
                  }
                >
                  <button
                    type="button"
                    className="btn btn--danger"
                    onClick={() => reject(p)}
                    disabled={busy === p.template.templateId}
                    data-testid="approval-reject"
                  >
                    Reject
                  </button>
                </ActionBar>
              </div>
            ),
          }))}
        />
        </div>
      ))}
    </section>
  );
}

/**
 * Collapsible review-comment thread for a pending template (item 10). Comments are
 * lazy-loaded via `listComments(templateId)` the first time the thread is opened;
 * the add form posts via `addComment(...)` and prepends the result locally so the
 * thread stays live without a full tab refresh. Public/internal comments are
 * badged distinctly (customers only ever see `public`; `internal` is admin-only,
 * server-enforced).
 */
function CommentThread({ templateId, version }: { templateId: string; version: number }) {
  const { client } = useServices();
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [comments, setComments] = useState<ReviewComment[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [draftType, setDraftType] = useState<CommentType>("internal");
  const [posting, setPosting] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setComments(await client.listComments(templateId));
      setLoaded(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  // Load comments the first time the thread is expanded (cheap on re-open).
  function toggle() {
    const next = !open;
    setOpen(next);
    if (next && !loaded && !loading) void load();
  }

  async function add() {
    const text = draft.trim();
    if (!text) return;
    setPosting(true);
    setError(null);
    try {
      const created = await client.addComment(templateId, {
        commentType: draftType,
        commentText: text,
        version,
      });
      setComments((cs) => [created, ...cs]);
      setLoaded(true);
      setDraft("");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPosting(false);
    }
  }

  return (
    <div className="admin-tab__form-section" data-testid="approval-comments">
      <button
        type="button"
        className="btn btn--sm btn--ghost"
        onClick={toggle}
        aria-expanded={open}
        data-testid="approval-comments-toggle"
      >
        <Icon name={open ? "chevron-down" : "chevron-right"} size={16} /> Comments{comments.length ? ` (${comments.length})` : ""}
      </button>

      {open && (
        <div className="admin-tab__form-section">
          {error && <p role="alert">{error}</p>}
          {loading ? (
            <p className="faint" data-testid="approval-comments-loading">Loading comments…</p>
          ) : comments.length === 0 ? (
            <p className="faint" data-testid="approval-comments-empty">No comments yet.</p>
          ) : (
            <ul className="admin-tab__list" data-testid="approval-comments-list">
              {comments.map((c) => (
                <li key={c.id} className="admin-tab__card" data-testid="approval-comment">
                  <div className="row row--between">
                    <span
                      className={`badge ${c.commentType === "public" ? "badge--info" : "badge--neutral"}`}
                      data-testid="approval-comment-type"
                    >
                      {c.commentType === "public" ? "Public" : "Internal"}
                    </span>
                    {c.createdBy && <span className="faint">{c.createdBy}</span>}
                  </div>
                  <p>{c.commentText}</p>
                </li>
              ))}
            </ul>
          )}

          <div className="field">
            <label htmlFor={`comment-type-${templateId}`}>Add a comment</label>
            <div className="row">
              <select
                id={`comment-type-${templateId}`}
                value={draftType}
                onChange={(e) => setDraftType(e.target.value as CommentType)}
                data-testid="approval-comment-type-select"
              >
                <option value="internal">Internal</option>
                <option value="public">Public (visible to customer)</option>
              </select>
              <input
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Note for the designer or customer"
                data-testid="approval-comment-input"
              />
              <button
                type="button"
                /* WS-Z5: a sub-form action inside the review body, not the view's primary —
                   that is Approve, in the bar. */
                className="btn btn--sm"
                onClick={() => void add()}
                disabled={posting || !draft.trim()}
                data-testid="approval-comment-add"
              >
                Post
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
